<template>
  <dialog
    ref="pannello"
    :aria-label="ariaLabel"
    class="ui-drawer m-0 mr-auto h-dvh max-h-none w-[min(var(--larghezza-drawer),calc(100vw-3rem))] max-w-none overflow-visible bg-transparent p-0 text-text-primary"
    :style="{ '--larghezza-drawer': larghezza }"
    data-test="ui-drawer"
    @cancel="alAnnulla"
    @close="alChiuso"
    @click="alClic"
  >
    <!-- Il fuoco iniziale va a questo contenitore (autofocus + tabindex -1), non al primo campo: aprendo il menu non si deve
         finire dentro la ricerca (e su telefono non deve comparire la tastiera). -->
    <GlassPanel
      strong
      :glow="false"
      padding="none"
      tabindex="-1"
      autofocus
      class="flex h-full flex-col overflow-hidden rounded-l-none! rounded-r-xl! shadow-2xl outline-none"
      data-test="drawer-pannello"
    >
      <header v-if="$slots.header" class="shrink-0" data-test="drawer-testata"><slot name="header" /></header>
      <div class="flex min-h-0 flex-1 flex-col" data-test="drawer-corpo"><slot /></div>
      <footer v-if="$slots.footer" class="shrink-0" data-test="drawer-piede"><slot name="footer" /></footer>
    </GlassPanel>
  </dialog>
</template>

<script setup lang="ts">
// Pannello laterale modale (sostituisce Drawer di PrimeVue), come UiDialog un <dialog> nativo con showModal(): focus
// intrappolato, Esc chiude, il focus torna a chi l'ha aperto. Il nome accessibile è obbligatorio (non ha un titolo proprio).
// Slot: `header`, contenuto, `footer`. Uso: v-model:aperto.
// Animazione: entra scorrendo da sinistra con un lieve rimbalzo mentre sfondo e sfocatura salgono; esce più in fretta, senza
// rimbalzo. Usa transizioni CSS con @starting-style (dove il browser non le supporta il pannello compare senza animarsi).
const props = withDefaults(
  defineProps<{
    ariaLabel: string;
    larghezza?: string;
    chiudiConEsc?: boolean;
    chiudiSuSfondo?: boolean;
  }>(),
  { larghezza: "20rem", chiudiConEsc: true, chiudiSuSfondo: true },
);

const aperto = defineModel<boolean>("aperto", { default: false });
const emit = defineEmits<{ chiuso: [] }>();
const pannello = ref<HTMLDialogElement | null>(null);

function sincronizza(): void {
  const el = pannello.value;
  if (!el) return;
  if (aperto.value && !el.open) el.showModal();
  else if (!aperto.value && el.open) el.close();
}

watch(aperto, sincronizza);
onMounted(sincronizza);
onBeforeUnmount(() => {
  if (pannello.value?.open) pannello.value.close();
});

function alAnnulla(evento: Event): void {
  evento.preventDefault();
  if (props.chiudiConEsc) aperto.value = false;
}

function alClic(evento: MouseEvent): void {
  if (props.chiudiSuSfondo && evento.target === pannello.value) aperto.value = false;
}

function alChiuso(): void {
  if (aperto.value) aperto.value = false;
  emit("chiuso");
}
</script>

<style scoped>
/* Stato chiuso = uscita: veloce e senza rimbalzo. Le transizioni usano quelle dello stato di destinazione. */
.ui-drawer {
  translate: -105% 0;
  opacity: 0;
  transition:
    translate 220ms var(--ease-standard),
    opacity 180ms var(--ease-standard),
    display 220ms allow-discrete,
    overlay 220ms allow-discrete;
}
.ui-drawer::backdrop {
  background: transparent;
  backdrop-filter: blur(0);
  transition:
    background-color 220ms var(--ease-standard),
    backdrop-filter 220ms var(--ease-standard),
    display 220ms allow-discrete,
    overlay 220ms allow-discrete;
}

/* Stato aperto = entrata: più lenta, con il lieve rimbalzo */
.ui-drawer[open] {
  translate: 0 0;
  opacity: 1;
  transition:
    translate 520ms var(--ease-spring),
    opacity 260ms var(--ease-emphasized),
    display 520ms allow-discrete,
    overlay 520ms allow-discrete;
}
.ui-drawer[open]::backdrop {
  background: color-mix(in srgb, var(--color-graphite) 45%, transparent);
  backdrop-filter: blur(4px);
  transition-duration: 380ms;
}

@starting-style {
  .ui-drawer[open] {
    translate: -105% 0;
    opacity: 0;
  }
  .ui-drawer[open]::backdrop {
    background: transparent;
    backdrop-filter: blur(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ui-drawer,
  .ui-drawer[open],
  .ui-drawer::backdrop,
  .ui-drawer[open]::backdrop {
    transition-duration: 0.01ms;
  }
}
</style>
